import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { Discount, DiscountSchema } from "./discount"
import { Receipt } from "./receipt"
import { ServiceAction } from "./service-part-action"

export enum DiscountAppliesTo {
  LABOR = "LABOR",
  PRODUCTS = "PRODUCTS",
  BOTH = "BOTH",
  SERVICE_PACKAGE = "SERVICE_PACKAGE",
}

export const DiscountExcludedPartActionSchema = z.object({
  partId: z.guid(),
  action: z.nativeEnum(ServiceAction),
})

/**
 * What a code does not discount. Only meaningful for codes on the whole order,
 * labour or products; a `SERVICE_PACKAGE` code discounts one package anyway.
 */
export const DiscountExclusionsSchema = z.object({
  /** `originalPackageId`s, i.e. the store's package templates. */
  packageIds: z.array(z.guid()),
  /** Matches standalone part actions only; a package's own are in its price. */
  partActions: z.array(DiscountExcludedPartActionSchema),
})

export type DiscountExcludedPartAction = z.infer<
  typeof DiscountExcludedPartActionSchema
>
export type DiscountExclusions = z.infer<typeof DiscountExclusionsSchema>

/** A part action a discount code can exclude, as the store's price list has it. */
export interface ExcludableDiscountPartAction
  extends DiscountExcludedPartAction {
  /** E.g. "Byt kedja". */
  label: string
}

export const NO_DISCOUNT_EXCLUSIONS: DiscountExclusions = {
  packageIds: [],
  partActions: [],
}

export const DiscountCodeSchema = z.object({
  id: z.guid(),
  name: z.string().min(1).max(50),
  discount: DiscountSchema.refine((discount) => discount.value >= 1, {
    message: "common.validation.discountAtLeastOne" satisfies TranslationKey,
  }),
  appliesTo: z.nativeEnum(DiscountAppliesTo),
  servicePackageId: z.guid().nullable(),
  servicePackageName: z.string().nullable(),
  expiresAt: z.coerce.date<Date>().nullable(),
  usageLimit: z.number().int().min(1).nullable(),
  isActive: z.boolean(),
  exclusions: DiscountExclusionsSchema.default(NO_DISCOUNT_EXCLUSIONS),
})

const BaseCreateDiscountCodeSchema = DiscountCodeSchema.omit({
  id: true,
  servicePackageName: true,
})

export const CreateDiscountCodeSchema = BaseCreateDiscountCodeSchema.refine(
  (data) => {
    if (data.appliesTo === DiscountAppliesTo.SERVICE_PACKAGE) {
      return data.servicePackageId != null
    }
    return true
  },
  {
    message: "servicePackageId is required when appliesTo is SERVICE_PACKAGE",
    path: ["servicePackageId"],
  },
)
// Zod 4 applies .default() to missing keys even after .partial(), so the
// defaulted fields are made plain optional again: a PATCH that leaves them out
// must not reset them.
export const UpdateDiscountCodeSchema =
  BaseCreateDiscountCodeSchema.partial().extend({
    exclusions: DiscountExclusionsSchema.optional(),
  })

export const ValidateDiscountCodeSchema = z.object({
  code: z.string().min(1),
})

export interface JobDiscountFields {
  orderDiscount: Discount | null
  laborDiscount: Discount | null
  productDiscount: Discount | null
}

/**
 * How a code lands on the job's discount fields. `SERVICE_PACKAGE` deliberately
 * sets none of them: it discounts one package's labour and is resolved where
 * that package's price is known, not on the order as a whole.
 */
export const toJobDiscounts = (code: {
  appliesTo: DiscountAppliesTo
  discount: Discount
}): JobDiscountFields => ({
  orderDiscount:
    code.appliesTo === DiscountAppliesTo.BOTH ? code.discount : null,
  laborDiscount:
    code.appliesTo === DiscountAppliesTo.LABOR ? code.discount : null,
  productDiscount:
    code.appliesTo === DiscountAppliesTo.PRODUCTS ? code.discount : null,
})

export interface PackageDiscountFromCode {
  packageId: string
  discount: Discount
}

/**
 * The package a `SERVICE_PACKAGE` code discounts, or `null` for every other
 * kind. The id is an `originalPackageId` — what a proposal and a receipt both
 * key their packages by.
 */
export const getPackageDiscountFromCode = (code: {
  appliesTo: DiscountAppliesTo
  discount: Discount
  servicePackageId: string | null
}): PackageDiscountFromCode | null => {
  if (
    code.appliesTo !== DiscountAppliesTo.SERVICE_PACKAGE ||
    code.servicePackageId === null
  ) {
    return null
  }
  return {
    packageId: code.servicePackageId,
    discount: code.discount,
  }
}

/** Whether `exclusions` keeps a standalone part action at full price. */
export const isExcludedPartAction = (
  exclusions: DiscountExclusions,
  partId: string,
  action: ServiceAction,
): boolean =>
  exclusions.partActions.some(
    (pa) => pa.partId === partId && pa.action === action,
  )

/**
 * Flags the receipt lines `exclusions` keeps out of the job's labour, products
 * and order discounts. Packages match on `originalPackageId`, standalone part
 * actions on part and action.
 */
export const markDiscountExclusions = <
  T extends Pick<Receipt, "packages" | "partActions">,
>(
  receipt: T,
  exclusions: DiscountExclusions,
): T => {
  const { packageIds, partActions } = exclusions
  if (packageIds.length === 0 && partActions.length === 0) return receipt
  const excludedPackageIds = new Set(packageIds)

  return {
    ...receipt,
    packages: receipt.packages.map((pkg) =>
      excludedPackageIds.has(pkg.originalPackageId)
        ? { ...pkg, isExcludedFromJobDiscounts: true }
        : pkg,
    ),
    partActions: receipt.partActions.map((pa) =>
      isExcludedPartAction(exclusions, pa.partId, pa.partAction)
        ? { ...pa, isExcludedFromJobDiscounts: true }
        : pa,
    ),
  }
}

export type DiscountCode = z.infer<typeof DiscountCodeSchema>
export type CreateDiscountCode = z.infer<typeof CreateDiscountCodeSchema>
export type UpdateDiscountCode = z.infer<typeof UpdateDiscountCodeSchema>
export type ValidateDiscountCode = z.infer<typeof ValidateDiscountCodeSchema>

export interface DiscountCodeWithUsageCount extends DiscountCode {
  usageCount: number
  /** E.g. "Däckbyte", "Byt kedja": what `exclusions` keeps out, for display. */
  exclusionLabels: string[]
}

export interface DiscountCodeStats {
  id: string
  name: string
  discount: Discount
  appliesTo: DiscountAppliesTo
  servicePackageId: string | null
  servicePackageName: string | null
  jobCount: number
  totalRevenue: number
  totalDiscountGiven: number
  isActive: boolean
  usageLimit: number | null
  expiresAt: Date | null
}

export interface ValidatedDiscountCode {
  id: string
  name: string
  discount: Discount
  appliesTo: DiscountAppliesTo
  servicePackageId: string | null
  servicePackageName: string | null
  exclusions: DiscountExclusions
  isValid: boolean
  errorMessage: string | null
}
